/* eslint-disable camelcase */
import {
    fetchAuthSession,
    fetchUserAttributes,
    signIn,
    signUp,
    signOut,
    updateUserAttributes,
    updatePassword,
    resetPassword,
    confirmResetPassword,
    confirmSignUp,
    resendSignUpCode,
    sendUserAttributeVerificationCode,
    confirmUserAttribute,
    confirmSignIn,
} from 'aws-amplify/auth';
import { UpdateUserAttributes, UserInfo, UserSession, UserInfoFromSession, SignupProps } from './types';
import { updateLastUsedFrontend } from '../users';

const getSession = async (): Promise<UserSession | undefined> => {
    try {
        const session = await fetchAuthSession();
        // Map v6 session to our UserSession interface
        const accessTokenPayload = session.tokens?.accessToken?.payload;
        if (!accessTokenPayload) return undefined;
        return {
            accessToken: {
                payload: {
                    sub: accessTokenPayload.sub as string,
                    'cognito:groups': accessTokenPayload['cognito:groups'] as string[] | undefined,
                }
            },
            tokens: session.tokens as any,
        } as UserSession;
    }
    catch (error) {
        return undefined;
    }
};

const getSubFromSession = (session: UserSession): string | undefined => {
    return session?.accessToken?.payload?.sub;
};

const getRoleFromSession = (session: UserSession): string => {
    const groups: string[] = session?.accessToken?.payload?.['cognito:groups'] || [];
    return groups.includes('admin') ? 'admin' : 'user';
};

const getCurrentUserInfo = async (): Promise<UserInfo> => {
    const attrs = await fetchUserAttributes();
    return {
        attributes: attrs as any,
    };
};

const getUserInfoFromSession = async (): Promise<UserInfoFromSession> => {
    const session: UserSession | undefined = await getSession();
    if (session) {
        const sub = getSubFromSession(session);
        if (sub) {
            const role = getRoleFromSession(session);
            const { attributes } = await getCurrentUserInfo();
            return {
                sub,
                role,
                attributes
            };
        }
    }
    throw new Error('Invalid user session');
};

const getCurrentCredentials = async () => {
    const session = await fetchAuthSession();
    return session.credentials;
};

const authLogin = async (email: string, password: string) => {
    const result = await signIn({ username: email, password });
    return result;
};

const authResendActivationEmail = async (email: string): Promise<void> => {
    await updateLastUsedFrontend(email);
    await resendSignUpCode({ username: email });
};

const authLogout = async () => {
    await signOut();
};

const authSignup = async ({ email, password, name, phone_number, 'custom:language': language, 'custom:role': role, isSVAMember }: SignupProps) => {
    const userAttributes: Record<string, string> = {
        phone_number,
        'custom:language': language,
        'custom:frontend': 'leea',
    };
    if (name) {
        userAttributes.name = name;
    }
    if (role) {
        userAttributes['custom:role'] = role;
    }

    const signupResult = await signUp({
        username: email,
        password,
        options: {
            userAttributes,
            ...(isSVAMember && { autoSignIn: false }),
        }
    });
    // Return v2-compatible shape so sagas can destructure { userSub }
    return { userSub: signupResult.userId };
};

const authUpdateUserInfo = async (userAttributes: UpdateUserAttributes) => {
    const attrs: Record<string, string> = {};
    for (const [key, value] of Object.entries(userAttributes)) {
        if (value !== undefined && value !== null) {
            attrs[key] = String(value);
        }
    }
    const result = await updateUserAttributes({
        userAttributes: attrs,
    });
    return result;
};

const authRenewPassword = async (_cognitoUser: any, password: string, _requiredAttributes: any) => {
    await confirmSignIn({ challengeResponse: password });
};

const authChangePassword = async (currentPassword: string, password: string) => {
    await updatePassword({ oldPassword: currentPassword, newPassword: password });
};

const authForgotPassword = async (email: string) => {
    await updateLastUsedFrontend(email);
    await resetPassword({ username: email });
};

const authForgotPasswordSubmit = async (email: string, code: string, password: string) => {
    await confirmResetPassword({ username: email, confirmationCode: code, newPassword: password });
};

const authConfirmSignUp = async (email: string, code: string) => {
    await updateLastUsedFrontend(email);
    await confirmSignUp({ username: email, confirmationCode: code });
};

const authResendSignUpVerificationCode = async (email: string) => {
    await updateLastUsedFrontend(email);
    await resendSignUpCode({ username: email });
};

const authVerifyEmail = async () => {
    await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
};

const authVerifyEmailSubmit = async (code: string) => {
    await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code });
};

const authLoginErrorCodes = (errorCode: string) => {
    switch (errorCode) {
        case 'UserNotConfirmedException':
            return `login.${errorCode}`;
        case 'NotAuthorizedException':
            return `login.${errorCode}`;
        case 'UserNotFoundException':
            return `login.${errorCode}`;
        default:
            return 'login.error';
    }
};

export { getSession, getUserInfoFromSession, getCurrentUserInfo, getCurrentCredentials, authLogin, authResendActivationEmail, authLogout, authSignup, authUpdateUserInfo, authRenewPassword, authChangePassword, authForgotPassword, authForgotPasswordSubmit, authVerifyEmail, authVerifyEmailSubmit, authConfirmSignUp, authResendSignUpVerificationCode, authLoginErrorCodes };
